import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import SpinModal from './SpinModal';
import { SUPABASE_URL, supabaseHeaders } from '../supabase';
import { uploadPoster } from '../utils/imageUpload';
import { useAuth } from '../AuthContext';

function TodoMoviesList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [reversed, setReversed] = useState(false);
  const [search, setSearch] = useState('');
  const [filterJoy, setFilterJoy] = useState('all');
  const [showSpin, setShowSpin] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addJoy, setAddJoy] = useState(false);
  const [addPoster, setAddPoster] = useState(null);
  const [addPosterPreview, setAddPosterPreview] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [addStatus, setAddStatus] = useState('');

  const [editingItem, setEditingItem] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editJoy, setEditJoy] = useState(false);
  const [editPoster, setEditPoster] = useState(null);
  const [editPosterPreview, setEditPosterPreview] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editStatus, setEditStatus] = useState('');

  const fetchItems = async () => {
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/todo_movies?order=created_at.desc&select=*`, {
        headers: supabaseHeaders(false),
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setItems(data || []);
    } catch (err) {
      console.error('Failed to fetch todo movies:', err);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const shuffle = () => setItems((prev) => [...prev].sort(() => Math.random() - 0.5));

  const openAdd = () => { setAddTitle(''); setAddJoy(false); setAddPoster(null); setAddPosterPreview(null); setAddStatus(''); setShowAdd(true); };
  const closeAdd = () => setShowAdd(false);

  const openEdit = (item) => {
    setEditingItem(item);
    setEditTitle(item.title);
    setEditJoy(item.joy || false);
    setEditPoster(null);
    setEditPosterPreview(null);
    setEditStatus('');
  };

  const closeEdit = () => setEditingItem(null);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editTitle) { setEditStatus('Enter a title.'); return; }
    setEditLoading(true);
    setEditStatus('');
    try {
      let posterUrl = null;
      if (editPoster) posterUrl = await uploadPoster(editPoster);

      const payload = { title: editTitle, joy: editJoy };
      if (posterUrl) payload.poster_url = posterUrl;

      const res = await fetch(`${SUPABASE_URL}/rest/v1/todo_movies?id=eq.${editingItem.id}`, {
        method: 'PATCH',
        headers: supabaseHeaders(true),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Update failed');

      setEditStatus('Saved :D');
      setTimeout(() => { setEditingItem(null); fetchItems(); }, 1000);
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditDelete = async () => {
    if (!window.confirm(`Delete "${editingItem.title}"?`)) return;
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/todo_movies?id=eq.${editingItem.id}`, {
        method: 'DELETE',
        headers: supabaseHeaders(true),
      });
      if (!res.ok) throw new Error('Delete failed');
      setEditingItem(null);
      fetchItems();
    } catch (err) {
      setEditStatus(`Something went wrong: ${err.message}`);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addTitle) { setAddStatus('Enter a title.'); return; }
    setAddLoading(true);
    setAddStatus('');
    try {
      let posterUrl = null;
      if (addPoster) posterUrl = await uploadPoster(addPoster);
      const payload = { title: addTitle, joy: addJoy };
      if (posterUrl) payload.poster_url = posterUrl;

      const res = await fetch(`${SUPABASE_URL}/rest/v1/todo_movies`, {
        method: 'POST',
        headers: { ...supabaseHeaders(true), Prefer: 'return=representation' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to create');

      setAddStatus('Saved :D');
      setTimeout(() => { setShowAdd(false); fetchItems(); }, 1000);
    } catch (err) {
      setAddStatus(`Something went wrong: ${err.message}`);
    } finally {
      setAddLoading(false);
    }
  };

  const displayed = (reversed ? [...items].reverse() : items)
    .filter(i => i.title.toLowerCase().includes(search.toLowerCase()))
    .filter(i => filterJoy === 'all' || (filterJoy === 'joy' ? i.joy === true : i.joy !== true));

  return (
    <div className="app-container">
      <div className="header">
        <div className="header-title-row">
          <h1 onClick={() => navigate('/Movies')} style={{ cursor: 'pointer' }}>Movie Reviews</h1>
          <span className="header-subtitle">To-Do</span>
        </div>
        <div className="controls">
          {user && <button onClick={openAdd}>Add +</button>}
          <input type="text" placeholder="Search by title..." value={search} onChange={(e) => setSearch(e.target.value)} />
          <select value={filterJoy} onChange={(e) => setFilterJoy(e.target.value)}>
            <option value="all">Joy filter</option>
            <option value="joy">Joy &lt;3</option>
            <option value="no-joy">No Joy</option>
          </select>
          <button onClick={shuffle}>Randomize</button>
          <button onClick={() => setReversed((r) => !r)} className={reversed ? 'btn-active' : ''}>{reversed ? 'Newest' : 'Oldest'}</button>
          <button onClick={() => setShowSpin(true)}>Spin</button>
        </div>
      </div>

      <div className="todo-grid">
        {displayed.map((item, idx) => (
          <div
            className="todo-card"
            key={item.id}
            style={{ '--index': idx }}
            onDoubleClick={user ? () => navigate('/add-movie', { state: { title: item.title, posterUrl: item.poster_url, todoItemId: item.id, todoType: 'movie' } }) : undefined}
          >
            {item.poster_url && (
              <img src={item.poster_url} alt={item.title} className="todo-card-poster" />
            )}
            <div
              className="todo-card-title"
              onDoubleClick={user ? (e) => { e.stopPropagation(); openEdit(item); } : undefined}
            >
              {item.title}
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && <p className="no-results">Geen ene tering hier man.</p>}

      {showAdd && (
        <Modal onClose={closeAdd}>
          <h2 className="modal-title">Add</h2>
          <form className="modal-form" onSubmit={handleAdd}>
            <div className="form-group">
              <label>Title *</label>
              <input type="text" value={addTitle} onChange={(e) => setAddTitle(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Poster</label>
              {addPosterPreview && <img src={addPosterPreview} alt="preview" className="poster-preview" />}
              <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; setAddPoster(f); setAddPosterPreview(f ? URL.createObjectURL(f) : null); }} />
            </div>
            <div className="form-group">
              <label><input type="checkbox" checked={addJoy} onChange={(e) => setAddJoy(e.target.checked)} /> Joy &lt;3</label>
            </div>
            {addStatus && <p className="form-status">{addStatus}</p>}
            <div className="edit-modal-actions">
              <button type="submit" className="form-submit" disabled={addLoading}>{addLoading ? 'Loading...' : 'Add'}</button>
              <button type="button" className="form-submit btn-cancel" onClick={closeAdd}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {editingItem && (
        <Modal onClose={closeEdit}>
          <h2 className="modal-title">Edit</h2>
          <form className="modal-form" onSubmit={handleEditSubmit}>
            <div className="form-group">
              <label>Title *</label>
              <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Poster</label>
              {(editPosterPreview || (editingItem.poster_url && !editPoster)) && (
                <img src={editPosterPreview || editingItem.poster_url} alt="preview" className="poster-preview" />
              )}
              <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files[0]; setEditPoster(f); setEditPosterPreview(f ? URL.createObjectURL(f) : null); }} />
            </div>
            <div className="form-group">
              <label><input type="checkbox" checked={editJoy} onChange={(e) => setEditJoy(e.target.checked)} /> Joy &lt;3</label>
            </div>
            {editStatus && <p className="form-status">{editStatus}</p>}
            <div className="edit-modal-actions">
              <button type="button" className="form-submit btn-cancel" onClick={closeEdit}>Cancel</button>
              <button type="button" className="form-submit btn-delete" onClick={handleEditDelete}>Delete</button>
              <button type="submit" className="form-submit" disabled={editLoading}>
                {editLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showSpin && <SpinModal items={displayed} onClose={() => setShowSpin(false)} />}
    </div>
  );
}

export default TodoMoviesList;
